import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { StartCheckoutPreference, UserType } from '@platform-frontend/checkout';
import {
  BillingInformation,
  CheckoutLocale,
  GroupContext,
  LinkedAccount,
  ManagedContract,
  Metadata,
  RedirectHandling,
  SubscriptionInterval,
  Term,
  sanitizeEnvironment,
  sanitizeProductId,
  sanitizeVendorId
} from '@platform-frontend/checkout-drop-in';
import { ProductIdentifier } from '@platform-frontend/marketplace';
import { LocaleSetupService } from '@platform-frontend/transloco';
import { InvalidStateError } from '@platform-frontend/util';
import {
  GeneralStartMessageParamsWithMetadata,
  StartAcademicRegistrationMessageParams,
  StartCheckoutMessageParams,
  StartSignInMessageParams
} from './model/web-checkout-sanitized-start-params';

@Injectable({
  providedIn: 'root'
})
export class WebCheckoutStartParamsSanitizerService {
  constructor(private localeSetupService: LocaleSetupService) {}

  sanitizeAcademicParams(params: any): StartAcademicRegistrationMessageParams {
    this.mapUserTypeForBackwardCompatibility(params);
    const generalParams = this.sanitizeGeneralParams(params);
    return {
      productIdentifier: { productId: sanitizeProductId(params?.productId, 'Academic registration without productId not yet supported.'), environment: 'LIVE' },
      start: { type: this.sanitizeUserType(params?.start?.type) },
      ...generalParams
    };
  }

  private mapUserTypeForBackwardCompatibility(params: any): void {
    if (typeof params?.start?.type === 'string') {
      params.start.type = params.start.type.toUpperCase();
    }
  }

  private sanitizeUserType(userType: any): UserType {
    if (userType !== undefined && userType !== null && userType !== 'STUDENT' && userType !== 'TEACHER' && userType !== 'ORGANIZATION') {
      throw new InvalidStateError(`Type ${JSON.stringify(userType)} not supported.`);
    }
    return userType ?? 'STUDENT';
  }

  sanitizeCheckoutParams(params: any): StartCheckoutMessageParams {
    this.mapCheckoutParamsForBackwardCompatibility(params);

    const productIdentifier: ProductIdentifier = { productId: sanitizeProductId(params?.productId), environment: sanitizeEnvironment(params?.environment) };
    const manageContract = this.sanitizeManageContract(params?.manageContract);
    const start = this.sanitizeCheckoutStart(params?.start);
    const generalParams = this.sanitizeGeneralParams(params);
    return { productIdentifier, contractId: manageContract?.contractId ?? null, manageAssetHint: manageContract !== null, start, ...generalParams };
  }

  private sanitizeManageContract(manageContract: any): ManagedContract | null {
    if (manageContract !== undefined && manageContract !== null && typeof manageContract !== 'object') {
      throw new InvalidStateError(`ManageContract ${JSON.stringify(manageContract)} not supported.`);
    }
    if (!manageContract) return null;
    const contractId = manageContract.contractId;
    if (contractId !== undefined && contractId !== null && typeof contractId !== 'string') {
      throw new InvalidStateError(`ContractId ${JSON.stringify(contractId)} not supported.`);
    }
    return manageContract;
  }

  private sanitizeCheckoutStart(start: any): StartCheckoutPreference | null {
    if (start?.type !== undefined && start?.type !== null && start?.type !== 'ONE_TIME_PURCHASE' && start?.type !== 'SUBSCRIPTION') {
      throw new InvalidStateError(`Type ${JSON.stringify(start?.type)} not supported.`);
    }
    if (!start) return null;
    if (start.type === 'SUBSCRIPTION') {
      return { quantity: 1, kind: { type: 'SUBSCRIPTION', subscriptionInterval: this.sanitizeSubscriptionInterval(start.subscriptionInterval) } };
    } else if (start.type === 'ONE_TIME_PURCHASE') {
      return { quantity: this.sanitizeQuantity(start.quantity), kind: { type: 'ONE_TIME_PURCHASE', term: this.sanitizeTerm(start.term) } };
    }
    return { quantity: this.sanitizeQuantity(start.quantity), kind: null };
  }

  private sanitizeSubscriptionInterval(subscriptionInterval: any): SubscriptionInterval | null {
    if (
      subscriptionInterval !== undefined &&
      subscriptionInterval !== null &&
      subscriptionInterval !== 'MONTHLY' &&
      subscriptionInterval !== 'QUARTERLY' &&
      subscriptionInterval !== 'YEARLY'
    ) {
      throw new InvalidStateError(`SubscriptionInterval ${JSON.stringify(subscriptionInterval)} not supported.`);
    }

    return subscriptionInterval ?? null;
  }

  private sanitizeQuantity(quantity: any): number | null {
    if (quantity !== undefined && quantity !== null && typeof quantity !== 'number') {
      throw new InvalidStateError(`Quantity ${JSON.stringify(quantity)} not supported.`);
    }
    return quantity ?? null;
  }

  private sanitizeTerm(term: any): Term | null {
    if (term !== undefined && term !== null && term !== 'UNLIMITED') {
      if (
        typeof term.runtime !== 'number' &&
        term.unit !== 'MINUTE' &&
        term.unit !== 'HOUR' &&
        term.unit !== 'DAY' &&
        term.unit !== 'WEEK' &&
        term.unit !== 'MONTH' &&
        term.unit !== 'YEAR' &&
        term.startOfTerm !== undefined &&
        term.startOfTerm !== null &&
        typeof term.startOfTerm !== 'string'
      ) {
        throw new InvalidStateError(`Term ${JSON.stringify(term)} not supported.`);
      }

      return { startOfTerm: term.startOfTerm ?? null, ...term };
    }

    return term ?? null;
  }

  private sanitizeBoolean(booleanValue: any, paramName: string): boolean {
    if (booleanValue !== undefined && booleanValue !== null && booleanValue !== true && booleanValue !== false) {
      throw new InvalidStateError(paramName + ` ${JSON.stringify(booleanValue)} not supported.`);
    }
    return booleanValue ?? false;
  }

  private mapLicenseTypeForBackwardCompatibility(params: any): void {
    if (typeof params?.start?.type === 'string') {
      params.start.type === 'oneTimePurchase' ? (params.start.type = 'ONE_TIME_PURCHASE') : (params.start.type = params.start.type.toUpperCase());
    }
  }

  private mapSubscriptionIntervalForBackwardCompatibility(params: any): void {
    if (typeof params?.start?.subscriptionInterval === 'string') {
      params.start.subscriptionInterval = params.start.subscriptionInterval.toUpperCase();
    }
  }

  private mapTermForBackwardCompatibility(params: any): void {
    if (typeof params?.start?.term === 'string') {
      params.start.term = params.start.term.toUpperCase();
      return;
    }

    if (typeof params?.start?.term?.unit === 'string') {
      params.start.term.unit = params.start.term.unit.toUpperCase();
    }
  }

  private mapCheckoutParamsForBackwardCompatibility(params: any): void {
    this.mapLicenseTypeForBackwardCompatibility(params);
    this.mapSubscriptionIntervalForBackwardCompatibility(params);
    this.mapTermForBackwardCompatibility(params);
  }

  sanitizeMetadataId(metadataId: any): string | null {
    if (metadataId !== undefined && metadataId !== null && typeof metadataId !== 'string') {
      throw new InvalidStateError(`MetadataId ${JSON.stringify(metadataId)} not supported.`);
    }

    return metadataId ?? null;
  }

  sanitizeMetadata(metadata: any): Metadata | null {
    if (metadata !== undefined && metadata !== null && typeof metadata !== 'object') {
      throw new InvalidStateError(`Metadata ${JSON.stringify(metadata)} not supported.`);
    }

    if (!metadata) return null;
    const customMetadata = this.getSanitizedCustomMetadata(metadata?.customMetadata);
    const onSuccess = this.getRedirectHandling(metadata?.onSuccess, 'On success');
    const onClose = this.getRedirectHandling(metadata?.onClose, 'On close');

    // JUST FOR BACKWARD COMPATIBILITY:
    const billingInformation = this.getBillingInformation(metadata?.billingInformation);
    const groupContext = this.getGroupContext(metadata?.groupContext);

    return { customMetadata, onClose, onSuccess, billingInformation, groupContext };
  }

  private getSanitizedCustomMetadata(customMetadata: any): Record<string, any> | null {
    if (customMetadata !== undefined && customMetadata !== null && typeof customMetadata !== 'object') {
      throw new InvalidStateError(`Custom metadata ${JSON.stringify(customMetadata)} not supported.`);
    }
    if (typeof customMetadata === 'object') {
      const keys = Object.keys(customMetadata);
      if (keys.length > 64) {
        throw new InvalidStateError(`Custom metadata has too many keys. Maximum is 64.`);
      }

      if (keys.some(key => typeof key !== 'string' || key.length > 42)) {
        throw new InvalidStateError(`Custom metadata keys must have maximum 42 characters.`);
      }

      if (Object.values(customMetadata).some(value => JSON.stringify(value).length > 1024)) {
        throw new InvalidStateError(`Custom metadata length for metadata values (stringified) is 1024.`);
      }
    }
    return customMetadata ?? null;
  }

  private getGroupContext(groupContext: any): GroupContext | null {
    if (
      (groupContext !== undefined && groupContext !== null && typeof groupContext !== 'object') ||
      (typeof groupContext === 'object' && (typeof groupContext.name !== 'string' || typeof groupContext.id !== 'string'))
    ) {
      throw new InvalidStateError(`Group context ${JSON.stringify(groupContext)} not supported.`);
    }
    return groupContext ?? null;
  }

  private getRedirectHandling(redirectHandling: any, redirectHandlingError: string): RedirectHandling | null {
    if (
      (redirectHandling !== undefined && redirectHandling !== null && typeof redirectHandling !== 'object') ||
      (typeof redirectHandling === 'object' && this.isRedirectUrlFaulty(redirectHandling))
    ) {
      throw new InvalidStateError(`${redirectHandlingError} ${JSON.stringify(redirectHandling)} not supported.`);
    }
    return redirectHandling ?? null;
  }

  private isRedirectUrlFaulty(redirectHandling: any): boolean {
    return redirectHandling.redirectUrl !== undefined && redirectHandling.redirectUrl !== null && typeof redirectHandling.redirectUrl !== 'string';
  }

  private getBillingInformation(billingInformation: any): BillingInformation | null {
    if (billingInformation !== undefined && billingInformation !== null && typeof billingInformation !== 'object') {
      throw new InvalidStateError(`Billing information ${JSON.stringify(billingInformation)} not supported.`);
    }

    if (typeof billingInformation !== 'object') return null;

    const firstName = billingInformation.firstName;
    if (firstName !== undefined && firstName !== null && typeof firstName !== 'string') {
      throw new InvalidStateError(`First name ${JSON.stringify(firstName)} not supported.`);
    }

    const lastName = billingInformation.lastName;
    if (lastName !== undefined && lastName !== null && typeof lastName !== 'string') {
      throw new InvalidStateError(`Last name ${JSON.stringify(lastName)} not supported.`);
    }

    const organizationName = billingInformation.organizationName;
    if (organizationName !== undefined && organizationName !== null && typeof organizationName !== 'string') {
      throw new InvalidStateError(`Organization name ${JSON.stringify(organizationName)} not supported.`);
    }

    const address = billingInformation.address;
    if (address !== undefined && address !== null && typeof address !== 'string') {
      throw new InvalidStateError(`Address ${JSON.stringify(address)} not supported.`);
    }

    const address2 = billingInformation.address2;
    if (address2 !== undefined && address2 !== null && typeof address2 !== 'string') {
      throw new InvalidStateError(`Address2 ${JSON.stringify(address2)} not supported.`);
    }

    const postalCode = billingInformation.postalCode;
    if (postalCode !== undefined && postalCode !== null && typeof postalCode !== 'string') {
      throw new InvalidStateError(`Postal code ${JSON.stringify(postalCode)} not supported.`);
    }

    const city = billingInformation.city;
    if (city !== undefined && city !== null && typeof city !== 'string') {
      throw new InvalidStateError(`City ${JSON.stringify(city)} not supported.`);
    }

    const province = billingInformation.province;
    if (province !== undefined && province !== null && typeof province !== 'string') {
      throw new InvalidStateError(`Province ${JSON.stringify(province)} not supported.`);
    }

    const country = billingInformation.country;
    if (country !== undefined && country !== null && typeof country !== 'string') {
      throw new InvalidStateError(`Country ${JSON.stringify(country)} not supported.`);
    }

    const email = billingInformation.email;
    if (email !== undefined && email !== null && (typeof email !== 'string' || !this.validateEmail(email))) {
      throw new InvalidStateError(`Email ${JSON.stringify(email)} not supported.`);
    }

    const vatId = billingInformation.vatId;
    if (vatId !== undefined && vatId !== null && typeof vatId !== 'string') {
      throw new InvalidStateError(`Vat id ${JSON.stringify(vatId)} not supported.`);
    }

    return { firstName, lastName, organizationName, address, address2, postalCode, city, province, country, email, vatId };
  }

  sanitizeSignInParams(params: any): StartSignInMessageParams {
    const generalParams = this.sanitizeGeneralParams(params);
    return {
      vendorId: sanitizeVendorId(params?.vendorId),
      ...generalParams
    };
  }

  private sanitizeGeneralParams(params: any): GeneralStartMessageParamsWithMetadata {
    const locale = this.sanitizeLocale(params?.locale);
    const metadataId = this.sanitizeMetadataId(params?.metadataId);
    const metadata = this.sanitizeMetadata(params?.metadata);
    const forceSignIn = this.sanitizeBoolean(params?.forceSignIn, 'ForceSignIn');
    return { locale, metadataId, metadata, forceSignIn };
  }

  private sanitizeLocale(locale: any): CheckoutLocale | null {
    if (locale !== undefined && locale !== null && typeof locale !== 'string' && !this.localeSetupService.isLocaleSupported(locale)) {
      throw new InvalidStateError(`Locale ${JSON.stringify(locale)} not supported.`);
    }
    return locale ?? null;
  }

  sanitizeLinkedAccount(linkedAccount: any): LinkedAccount | null {
    if (linkedAccount && (!linkedAccount.accountId || typeof linkedAccount.accountId !== 'string')) {
      throw new InvalidStateError(`AccountId ${JSON.stringify(linkedAccount.accountId)} not supported.`);
    }
    if (
      linkedAccount &&
      linkedAccount.email !== undefined &&
      linkedAccount.email !== null &&
      (typeof linkedAccount.email !== 'string' || !this.validateEmail(linkedAccount.email))
    ) {
      throw new InvalidStateError(`Email ${JSON.stringify(linkedAccount.email)} not supported.`);
    }
    return linkedAccount ?? null;
  }

  private validateEmail(email: string): boolean {
    const control = new FormControl(email, { validators: Validators.email });
    control.updateValueAndValidity();
    return control.errors === null;
  }
}
